const BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
};

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...opts.headers,
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method: opts.method || 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401) {
    localStorage.removeItem('auth_token');
    window.location.href = '/login';
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error');
    let message = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed.error) message = parsed.error;
    } catch {}
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export type User = {
  email: string;
  displayName: string;
  avatar?: string;
  subscriptionTier: string;
  applicationsToday: number;
  tailorsToday: number;
  preferences: {
    llmEndpoint?: string;
    llmModel?: string;
    autoApply: boolean;
  };
};

export type Resume = {
  id: string;
  name: string;
  isDefault: boolean;
  contactInfo: {
    fullName: string;
    email?: string;
    phone?: string;
    location?: string;
    linkedIn?: string;
    website?: string;
  };
  summary?: string;
  experience?: {
    company: string;
    title: string;
    location?: string;
    startDate: string;
    endDate?: string;
    current?: boolean;
    description?: string;
  }[];
  education?: {
    institution: string;
    degree: string;
    field?: string;
    startDate?: string;
    endDate?: string;
    gpa?: string;
  }[];
  skills?: string[];
  certifications?: string[];
  createdAt: string;
  updatedAt: string;
};

export type Job = {
  id: string;
  title: string;
  company: string;
  location?: string;
  url?: string;
  description?: string;
  salary?: string;
  jobType?: string;
  tags?: string[];
  source?: string;
  matchScore?: number;
  matchNotes?: string;
  missingSkills?: string[];
  createdAt: string;
};

export type Application = {
  id: string;
  resumeId: string;
  jobId: string;
  status: string;
  tailoredResume?: string;
  coverLetter?: string;
  appliedAt?: string;
  appliedVia?: string;
  notes?: string;
  createdAt: string;
};

export type Stats = {
  totalApplications: number;
  applicationsToday: number;
  dailyLimit: number;
  statusCounts: Record<string, number>;
};

export type TailorResponse = {
  tailoredResume: string;
  coverLetter: string;
  matchScore: number;
  matchNotes: string;
  missingSkills: string[];
};

export const userApi = {
  get: () => api.get<User>('/user'),
  update: (data: Partial<User>) => api.put<User>('/user', data),
  stats: () => api.get<Stats>('/stats'),
};

export const resumeApi = {
  list: () => api.get<Resume[]>('/resumes'),
  get: (id: string) => api.get<Resume>(`/resumes/${id}`),
  create: (data: Partial<Resume>) => api.post<Resume>('/resumes', data),
  update: (id: string, data: Partial<Resume>) => api.put<Resume>(`/resumes/${id}`, data),
  delete: (id: string) => api.delete(`/resumes/${id}`),
  setDefault: (id: string) => api.post(`/resumes/${id}/default`),
};

export const jobApi = {
  list: (params?: { limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return api.get<Job[]>(`/jobs${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => api.get<Job>(`/jobs/${id}`),
  create: (data: Partial<Job>) => api.post<Job>('/jobs', data),
  update: (id: string, data: Partial<Job>) => api.put<Job>(`/jobs/${id}`, data),
  delete: (id: string) => api.delete(`/jobs/${id}`),
};

export const applicationApi = {
  list: (params?: { status?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return api.get<Application[]>(`/applications${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => api.get<Application>(`/applications/${id}`),
  create: (data: Partial<Application>) => api.post<Application>('/applications', data),
  update: (id: string, data: Partial<Application>) => api.put<Application>(`/applications/${id}`, data),
  delete: (id: string) => api.delete(`/applications/${id}`),
};

export const tailorApi = {
  tailor: (data: { resumeId: string; jobDescription: string }) => api.post<TailorResponse>('/tailor', data),
  analyze: (data: { jobDescription: string; resumeId?: string }) => api.post<TailorResponse>('/analyze', data),
};

export { ApiError };
